
<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { useApi } from '@/composables/useApi'
import holidayService from '@/services/holiday.service'
import { useAbility } from '@casl/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction' // For clicking events
import FullCalendar from '@fullcalendar/vue3'
import { computed, ref } from 'vue'
import { VSkeletonLoader } from 'vuetify/lib/labs/components.mjs'

definePage({
  meta: {
    name: 'holiday-calender',
    subject: 'global.menu',
    action: 'holiday',
  },
})

const { can } = useAbility()
const holidayEvents = ref([])

const dialog = ref(false)
const confirmDialog = ref(false)
const deleteEvent = ref(null)
const holidayName = ref('')
const selectedDate = ref(null)
const selectedEvent = ref(null)
const isEditing = ref(false)

const unitId = ref(cookieRef('selectedUnit').value ?? null)
const forceKey = ref(0)

const {
  execute: fetchHolidays,
  isFetching: holidayLoading,
} = useApi(createUrl('/masters/holidays', {
  query: {
    unit_id: unitId,
  },
}), {
  afterFetch: ({ data }) => {
    holidayEvents.value = data.map(holiday => ({
      id: holiday.id,
      title: holiday.name,
      date: holiday.date,
    }))
    calendarOptions.value.events = holidayEvents.value
    forceKey.value++
  },
})

const canCreate = computed(() => can('create', 'master.holiday'))
const canUpdate = computed(() => can('update', 'master.holiday'))
const canDelete = computed(() => can('delete', 'master.holiday'))

const calendarOptions = ref({
  plugins: [dayGridPlugin, interactionPlugin],
  initialView: 'dayGridMonth',
  selectable: true,
  height: 'auto',
  fixedWeekCount: false,
  events: holidayEvents.value,
  headerToolbar: {
    left: 'prev,next',
    center: '',
    right: 'title',
  },

  dateClick(info) {
    if (canCreate.value) {
      selectedDate.value = info.dateStr
      holidayName.value = ''
      isEditing.value = false
      dialog.value = true
    }
  },
  eventContent(arg) {
    const title = arg.event.title
    const el = document.createElement('div')

    el.innerHTML = `
      <div class="holiday-item">
      <span class="holiday-label">${truncateText(title, 15)}</span>
      ${canDelete.value ? `<span class="holiday-remove" title="Remove Holiday">&times;</span>` : ''}
      </div>
    `

    if (canUpdate.value) {
      el.querySelector('.holiday-item').addEventListener('click', () => {
        selectedEvent.value = arg.event
        holidayName.value = title
        isEditing.value = true
        dialog.value = true
      })
    }

    if (canDelete.value) {
      el.querySelector('.holiday-remove').addEventListener('click', e => {
        e.stopPropagation()
        deleteEvent.value = arg.event
        confirmDialog.value = true
      })
    }

    return { domNodes: [el] }
  },

})

const localloading = ref(false)
const refForm = ref()

const confirmDeleteHoliday = async confirmed => {
  if (!confirmed || !deleteEvent.value) {
    deleteEvent.value = null

    return
  }
  deleteEvent.value.remove()
  await holidayService.deleteHoliday(deleteEvent.value.id)
  deleteEvent.value = null
  await fetchHolidays()
}

const saveHoliday = async () => {
  const { valid } = await refForm.value.validate()
  if (!valid) return

  localloading.value = true
  if (isEditing.value && selectedEvent.value) {
    selectedEvent.value.setProp('title', holidayName.value)

    const idx = holidayEvents.value.findIndex(ev => ev.date === selectedEvent.value.startStr)
    if (idx > -1) {
      await holidayService.updateHoliday(selectedEvent.value.id, { name: holidayName.value, date: selectedEvent.value.startStr, unit_id: unitId.value }, async () => {
        localloading.value = false
        dialog.value = false
        await fetchHolidays()
      }, () => {
        localloading.value = false
      })
    }
  } else {
    const form = { name: holidayName.value, date: selectedDate.value, unit_id: unitId.value }

    await holidayService.addHoliday(form, async () => {
      localloading.value = false
      dialog.value = false
      await fetchHolidays()
    }, () => {
      localloading.value = false
    })
  }
}
</script>

<template>
  <VCard
    variant="flat"
    class="border holiday-page-card pa-0"
  >
    <VCardText class="pa-0 holiday-calendar-wrapper">
      <VSkeletonLoader
        v-if="holidayLoading"
        type="card"
      />
      <FullCalendar
        v-else
        :key="forceKey"
        :options="calendarOptions"
        class="holiday-calendar"
      />
    </VCardText>

    <!-- Add/Edit Holiday Dialog -->
    <VDialog
      v-model="dialog"
      max-width="420"
      persistent
    >
      <VCard>
        <VCardItem class="py-3 px-4">
          <template #title>
            <div class="d-flex align-center gap-2">
              <IconBtn
                variant="tonal"
                color="primary"
              >
                <VIcon
                  icon="tabler-calendar-event"
                  size="22"
                  color="primary"
                />
              </IconBtn>
              <span class="text-h6 font-weight-medium">
                {{ isEditing ? 'Update Holiday' : 'Add Holiday' }}
              </span>
            </div>
          </template>
          <template #append>
            <IconBtn @click="dialog = false">
              <VIcon icon="tabler-x" />
            </IconBtn>
          </template>
        </VCardItem>

        <VDivider />

        <VCardText class="pa-4">
          <VForm
            ref="refForm"
            @submit.prevent="saveHoliday"
          >
            <AppTextField
              v-model="holidayName"
              label="Holiday Name"
              placeholder="Enter holiday name"
              required
              :rules="[requiredValidator]"
              autofocus
            />

            <div class="d-flex justify-end gap-4 mt-4">
              <VBtn
                color="secondary"
                variant="tonal"
                @click="dialog = false"
              >
                Cancel
              </VBtn>
              <VBtn
                color="primary"
                type="submit"
                variant="flat"
                :loading="localloading"
              >
                {{ isEditing ? 'Update' : 'Add' }}
              </VBtn>
            </div>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Delete Confirmation Dialog -->
    <ConfirmDialog
      v-model:is-dialog-visible="confirmDialog"
      confirmation-question="Are you sure you want to delete this holiday?"
      confirm-title="Delete Holiday"
      confirm-msg="This action cannot be undone."
      confirm-btn-color="error"
      @confirm="confirmDeleteHoliday"
    />
  </VCard>
</template>

<style lang="scss">
@use "@core/scss/template/libs/full-calendar";

// Flex chain: page card → card-text → calendar all stretch to fill viewport
.holiday-page-card {
  display: flex;
  flex-direction: column;
  block-size: 100%;
  overflow: hidden;
}

.holiday-calendar-wrapper {
  flex: 1;
  min-block-size: 0;
  overflow: hidden;
}

.holiday-calendar {
  block-size: 100%;

  .fc {
    block-size: 100%;
    display: flex;
    flex-direction: column;
  }

  .fc-view-harness {
    flex: 1;
    min-block-size: 0 !important;
  }

  .fc-toolbar-title {
    margin-inline-end: 0.5rem !important;
  }

  .fc-header-toolbar {
    margin: 0.5rem !important;
    margin-block-end: 0.5rem !important;
  }

  // Primary tonal color for holiday events
  .fc-event,
  .fc-daygrid-event {
    background-color: rgba(var(--v-theme-primary), 0.16);
    border-color: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    padding-block: 2px;
    padding-inline: 6px;

    &:hover {
      background-color: rgba(var(--v-theme-primary), 0.28);
    }
  }

  .fc-daygrid-event-dot {
    display: none;
  }

  // Responsive styles
  @media screen and (max-width: 600px) {
    .fc-header-toolbar {
      flex-direction: column;
      gap: 0.25rem;
      align-items: stretch !important;
    }

    .fc-toolbar-chunk {
      display: flex;
      justify-content: center;
    }

    .fc-toolbar-title {
      font-size: 1rem !important;
      text-align: center;
    }

    .fc-col-header-cell-cushion {
      font-size: 0.7rem !important;
      padding: 0.25rem !important;
    }

    .fc-daygrid-day-number {
      font-size: 0.75rem;
      padding: 0.25rem !important;
    }

    .fc-daygrid-day {
      padding: 1px !important;
    }

    .fc-event,
    .fc-daygrid-event {
      padding-block: 1px;
      padding-inline: 3px;
    }
  }

  @media screen and (max-width: 960px) and (min-width: 601px) {
    .fc-toolbar-title {
      font-size: 1.1rem !important;
    }

    .fc-col-header-cell-cushion {
      font-size: 0.75rem !important;
    }

    .fc-daygrid-day {
      padding: 2px !important;
    }
  }
}

.holiday-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.holiday-remove {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  margin-inline-start: 6px;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

.holiday-label {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;

  @media screen and (max-width: 600px) {
    font-size: 11px;
  }
}

.holiday-calendar .fc .fc-toolbar {
  justify-content: flex-end !important;
}
</style>
